import { lazy, type ComponentType } from 'react'

/**
 * Like React.lazy, but once `preload()` has finished the component renders synchronously
 * (React.lazy suspends for a tick even when the chunk is cached). That keeps focus inside
 * the tap on iOS, which is the only way it raises the keyboard.
 */
export function preloadable<P extends object>(load: () => Promise<{ default: ComponentType<P> }>) {
  let mod: { default: ComponentType<P> } | undefined
  let pending: Promise<{ default: ComponentType<P> }> | undefined
  const preload = () =>
    (pending ??= load().then(
      (m) => (mod = m),
      (e) => {
        pending = undefined
        throw e
      },
    ))
  const Lazy = lazy(preload)
  function Preloadable(props: P) {
    const C = mod?.default
    return C ? <C {...props} /> : <Lazy {...props} />
  }
  return Object.assign(Preloadable, { preload })
}
